import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserCircle, LogOut, ChevronDown } from 'lucide-react';
import { logout } from '../actions/authActions';
import { getLoggedInUserRole } from '../utils/auth';
import styles from './css/Header.module.css';

/**
 * Header Component - Top navigation bar matching sidebar theme
 * Displays admin panel title and profile dropdown with logout
 */
const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const slug = useMemo(() => {
    const pathParts = location.pathname.split('/');
    return pathParts[1] || 'root';
  }, [location.pathname]);

  const displayRole = useMemo(() => {
    const role = user?.role || getLoggedInUserRole();
    if (!role) {
      return 'Guest';
    }

    return role
      .split('_')
      .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }, [user]);

  const displayBalance = useMemo(() => {
    const role = user?.role || getLoggedInUserRole();
    if (role === 'super_admin') {
      return '∞';
    }

    let rawBalance = user?.balance;
    if (rawBalance === undefined || rawBalance === null) {
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        rawBalance = storedUser?.balance;
      } catch {
        rawBalance = null;
      }
    }

    const numericBalance = Number(rawBalance);
    if (!Number.isFinite(numericBalance)) {
      return '0.00';
    }

    return numericBalance.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }, [user]);

  /**
   * Handles user logout
   */
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    dispatch(logout());
    navigate(`/${slug}/login`);
  };

  /**
   * Toggles dropdown
   */
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  /**
   * Closes dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <header className={styles.header}>
      <div className={styles.headerContent}>
        <div className={styles.headerLeft}>
          <h1 className={styles.headerTitle}>Admin Panel</h1>
        </div>
        <div className={styles.headerRight} ref={dropdownRef}>
          <div className={styles.balancePill} aria-label="Logged in user balance">
            <span className={styles.balanceLabel}>Balance:</span>
            <span className={styles.balanceValue}>{displayBalance}</span>
          </div>
          <button
            className={styles.profileButton}
            onClick={toggleDropdown}
            aria-label="Profile menu"
            aria-expanded={dropdownOpen}
          >
            <UserCircle size={32} className={styles.profileIcon} />
            <ChevronDown 
              size={16} 
              className={`${styles.chevronIcon} ${dropdownOpen ? styles.chevronOpen : ''}`}
            />
          </button>
          
          {dropdownOpen && (
            <div className={styles.dropdown}>
              <div className={styles.dropdownHeader}>
                <span className={styles.dropdownWelcome}>Welcome,</span>
                <span className={styles.dropdownRole}>{displayRole}</span>
              </div>
              <div className={styles.dropdownDivider}></div>
              <button
                className={styles.logoutButton}
                onClick={handleLogout}
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
